﻿import { logError, logFatal, logInformation, logWarning } from '@core/logging';
import { ApiResponse, BaseApiResponse, SlideDeck, VindralChannelInformation, WebinarDataWithAssets } from '@core/swagger-client';
import {
    Chapter,
    ChatLinkPolicy,
    ChatMessage,
    ClientType,
    DisconnectReason,
    Exam,
    ExamResult,
    IngestType,
    LiveStatus,
    ParticipantAnswers,
    ParticipantSession,
    PlayerStatsSample,
    Poll,
    PollOptionResult,
    PollVoteSlim,
    RecorderStatus,
    SpeakerStatus,
    SpeakerStatusInfo,
    StreamValidation,
    Subtitle,
    SubtitlesTrack,
    Survey,
    VideoLayout,
    WebinarStatus
} from '@core/types';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Action } from '@reduxjs/toolkit';
import type { IWebrtcIngestStats } from '@vindral/webrtc-ingest-sdk';
import { Duration } from 'luxon';
import type { MoqPublisherStatsSample } from '../moq';
import type { LineProbeStatsBatch, SessionGrant } from '@core/line-probe';
import { PlayerStatsQueue } from './player-stats-queue';
import {
    ActivePollStatus,
    Booking,
    IngestInformation,
    activeChapterUpdated,
    activePollUpdated,
    activeSlideUpdated,
    bookingUpdated,
    chaptersUpdated,
    chatChannelUnreadMessages,
    chatMessage,
    chatMessageDeleted,
    dataConnectionStatusUpdated,
    examAnswersUpdated,
    examUpdated,
    initializedWebinar,
    joinedChatChannel,
    joinedWebinar,
    leftChatChannel,
    leftWebinar,
    liveStatusUpdated,
    meUpdated,
    myPollVotes,
    nicknameUpdated,
    notifiedSpeaker,
    pollVotesUpdated,
    pollsUpdated,
    presenterUpdated,
    recoderStatusUpdated,
    serverTimeUpdated,
    slideDeckUpdated,
    speakerStatusChanged,
    speakersUpdated,
    refreshVindralChannel as storeRefreshVindralChannel,
    streamValidationUpdated,
    ingestTypeHydrated,
    surveyAnswersUpdated,
    surveyUpdated,
    timestampUpdated,
    updateSubtitle,
    updateSubtitlesTracks,
    videoLayoutUpdated,
    vindralChannelUpdated,
    webinarDataUpdated
} from '../slices/live-webinar-slice';
import { chatLinkPolicyReceived } from '../slices/chat-link-policy-slice';
import { store } from '../store';

interface LiveState {
    me: ParticipantSession;
    webinarData: WebinarDataWithAssets;
    activeChapterId: string;
    chapters: Chapter[];
    activeSlideNumber: number;
    slideDeck: SlideDeck;
    activePollId: string;
    activePollStatus: ActivePollStatus;
    webinarStatus: WebinarStatus;
    liveStatus: LiveStatus;
    streamValidation: StreamValidation;
    ingestType: IngestType;
    videoLayout: VideoLayout;
    vindralChannelInformation: VindralChannelInformation;
    booking: Booking;
    survey: Survey;
    surveyAnswers: ParticipantAnswers;
    exam: Exam;
    examAnswers: ParticipantAnswers;
    pollVotes: PollVoteSlim[];
    subtitlesTracks: SubtitlesTrack[];
}

export interface ChatChannelUnreadMessageCount {
    channelId: string;
    noOfUnreadMessages: number;
}

export class LiveWebinarConnection {
    /** Set once by provideStoreRuntime; the hubs live at the root of the backend origin. */
    hubBaseUrl = '';

    isConnected: boolean = false;
    private disconnectReason?: DisconnectReason;
    private error?: BaseApiResponse;
    private connection?: HubConnection;

    private accessToken?: string;

    private readonly playerStats = new PlayerStatsQueue(
        (batch) => this.connection!.invoke('PlayerStats', batch),
        () => this.connection?.state === HubConnectionState.Connected
    );

    private readonly connectedCallbacks = new Set<() => void>();

    constructor() {
        if (globalThis.document) {
            globalThis.document.addEventListener('visibilitychange', () => {
                if (!this.isConnected || !this.connection) return;
                if (!globalThis.document.hidden) {
                    if (this.connection.state === HubConnectionState.Connected) {
                        // Verify if we're still connected
                        this.connection
                            .invoke('Ping')
                            .then(() => {
                                // Should we do something?
                            })
                            .catch(() => {
                                // Should we do something?
                            });
                    }
                }
            });
        }
    }

    dispatch(action: Action): void {
        store.dispatch(action);
        return;
    }

    connect(accessToken: string): Promise<boolean> {
        if (this.accessToken === accessToken) {
            // We are already connected with this access token
            return Promise.resolve(true);
        }
        this.accessToken = accessToken;

        // If we have a connection, close it before we start a new connection
        if (this.connection) {
            this.disconnect();
        }

        // Samples of a previous webinar must not be sent with the new session's labels
        this.playerStats.clear();

        this.connection = new HubConnectionBuilder()
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    // The first few attempts are nearly immediate: a dropped websocket is usually back
                    // within a moment, and every millisecond here counts against the server's grace
                    // period. After that, settle at 500ms and keep trying forever -- we handle the
                    // disconnect logic ourselves in "onreconnecting" and "onclose".
                    const ladder = [0, 100, 250];
                    return retryContext.previousRetryCount < ladder.length ? ladder[retryContext.previousRetryCount] : 500;
                }
            })
            .configureLogging({
                log: (logLevel, message) => {
                    if (message.indexOf('access_token') !== -1) {
                        // Remove the access token from the log message to avoid leaking it in the logs
                        message = message.replace(/access_token=([^&]+)/, 'access_token=***');
                    }

                    switch (logLevel) {
                        case LogLevel.Information:
                            logInformation('[LiveConnection] {message}', { message });
                            break;

                        case LogLevel.Warning:
                            logWarning('[LiveConnection] {message}', { message });
                            break;

                        case LogLevel.Error:
                            logError('[LiveConnection] {message}', { message });
                            break;

                        case LogLevel.Critical:
                            logFatal('[LiveConnection] {message}', { message });
                            break;
                    }
                }
            })
            .withUrl(`${this.hubBaseUrl}/live-webinar`, { accessTokenFactory: () => accessToken })
            .build();

        // Make a copy of the connection so we can perform some reference checking on the event dispatching
        const connection = this.connection;
        connection.onclose((error?) => {
            if (!this.connection || this.connection === connection) {
                this.dispatch(
                    dataConnectionStatusUpdated({
                        status: this.disconnectReason === DisconnectReason.Error ? LiveStatus.Error : LiveStatus.Offline,
                        error: this.error || { statusCode: 500, success: false },
                        disconnectReason: this.disconnectReason
                    })
                );
            }
        });
        connection.onreconnecting((error?) => {
            if (this.connection !== connection) return;
            this.dispatch(
                dataConnectionStatusUpdated({
                    status: this.disconnectReason === DisconnectReason.Error ? LiveStatus.Error : LiveStatus.Offline,
                    error: this.error || { statusCode: 500, success: false },
                    disconnectReason: this.disconnectReason
                })
            );
        });
        connection.onreconnected((connectionId?) => {
            if (this.connection !== connection) return;
            this.dispatch(dataConnectionStatusUpdated({ status: LiveStatus.Live }));
            this.notifyConnected();
        });

        connection.on('ServerTime', (serverTime: string) => {
            this.dispatch(serverTimeUpdated(serverTime));
            return;
        });

        connection.on('JoinedWebinar', (participants: ParticipantSession[]) => this.dispatch(joinedWebinar(participants)));
        connection.on('LeftWebinar', (participants: ParticipantSession[]) => this.dispatch(leftWebinar(participants)));

        connection.on('ChatMessage', (channelId: string, message: ChatMessage) => this.dispatch(chatMessage({ channelId, chatMessage: message })));
        connection.on('JoinedChatChannel', (channelId: string, messages: ChatMessage[], continued: boolean) => {
            this.dispatch(joinedChatChannel({ channelId, messages, continued, noOfUnreadMessages: 0 }));
        });
        connection.on('LeftChatChannel', (channelId: string) => this.dispatch(leftChatChannel(channelId)));
        connection.on('ChatChannelUnreadMessages', (channels: ChatChannelUnreadMessageCount[]) => this.dispatch(chatChannelUnreadMessages(channels)));
        connection.on('ChatMessageDeleted', (channelId: string, messageId: string, isPermanentlyDeleted: boolean) =>
            this.dispatch(chatMessageDeleted({ channelId, messageId, isPermanentlyDeleted }))
        );
        connection.on('ChatLinkPolicy', (policy: ChatLinkPolicy) => this.dispatch(chatLinkPolicyReceived(policy)));

        connection.on('WebinarDataChanged', (data: WebinarDataWithAssets) => this.dispatch(webinarDataUpdated(data)));

        connection.on('SlideDeckChanged', (slideDeck: SlideDeck) => this.dispatch(slideDeckUpdated(slideDeck)));
        connection.on('SlideChanged', (slideNumber: number) => this.dispatch(activeSlideUpdated(slideNumber)));

        connection.on('ChaptersChanged', (chapters: Chapter[]) => this.dispatch(chaptersUpdated(chapters)));
        connection.on('ChapterChanged', (chapterId: string) => this.dispatch(activeChapterUpdated(chapterId)));

        connection.on('PollsChanged', (polls: Poll[]) => {
            const results = polls.map((p) => {
                const result = {
                    ...p,
                    totalVotes: 0,
                    totalWeightedVotes: 0,
                    options: p.options.map((o) => {
                        return { ...o, votes: 0, weightedVotes: 0 };
                    })
                };
                return result;
            });
            this.dispatch(pollsUpdated(results));
        });
        connection.on('ActivePollChanged', (activePollId: string, activePollStatus: ActivePollStatus) =>
            this.dispatch(activePollUpdated({ activePollId, activePollStatus }))
        );
        connection.on('PollVotesChanged', (pollId: string, pollVotes: { [optionId: string]: PollOptionResult }) => {
            this.dispatch(pollVotesUpdated({ pollId, pollVotes }));
        });

        connection.on('LiveStatusChanged', (status: LiveStatus) => this.dispatch(liveStatusUpdated(status)));
        connection.on('StreamValidationChanged', (validation: StreamValidation) => this.dispatch(streamValidationUpdated(validation)));
        connection.on('SurveyChanged', (survey: Survey) => this.dispatch(surveyUpdated(survey)));
        connection.on('ExamChanged', (exam: Exam) => this.dispatch(examUpdated(exam)));
        connection.on('RecorderStatusChanged', (status: RecorderStatus) => this.dispatch(recoderStatusUpdated(status)));
        connection.on('NicknameChanged', (participantId: string, nickname: string) => {
            this.dispatch(nicknameUpdated({ participantId, nickname }));
        });
        connection.on('BookingChanged', (booking: Booking | null) => {
            this.dispatch(bookingUpdated(booking));
            if (booking) {
                this.dispatch(storeRefreshVindralChannel());
            }
        });
        connection.on('Disconnect', (reason: DisconnectReason, error?: BaseApiResponse) => {
            this.isConnected = false;
            this.disconnectReason = reason;
            this.error = error;

            this.dispatch(vindralChannelUpdated(null));
            connection.stop().then(() => {
                // Should we do something?
            });
        });
        connection.on('ActivePresenterChanged', (presenter: ParticipantSession | null) => this.dispatch(presenterUpdated(presenter)));

        connection.on('Speakers', (speakers: SpeakerStatusInfo[]) => this.dispatch(speakersUpdated(speakers)));
        connection.on('SpeakerStatusChanged', (participantId: string, status: SpeakerStatus) => this.dispatch(speakerStatusChanged({ participantId, status })));
        connection.on('NotifySpeaker', (participantId: string) => this.dispatch(notifiedSpeaker(participantId)));
        connection.on('ReportSpeaker', () => {
            const me = store.getState().liveWebinar.me;
            if (me?.clientType === ClientType.speaker) {
                this.setStatus(store.getState().liveWebinar.mySpeakerStatus);
            }
        });

        connection.on('VideoLayoutChanged', (layout: VideoLayout) => this.dispatch(videoLayoutUpdated(layout)));
        connection.on('SubtitlesTracksChanged', (tracks: SubtitlesTrack[]) => this.dispatch(updateSubtitlesTracks(tracks)));
        connection.on('SubtitleChanged', (trackId: string, subtitle: Subtitle) => this.dispatch(updateSubtitle({ trackId, subtitle })));
        connection.on('TimestampUpdated', (timestamp: string) => this.dispatch(timestampUpdated(timestamp)));
        connection.on('LiveState', (state: LiveState) => {
            this.dispatch(bookingUpdated(state.booking));
            this.dispatch(webinarDataUpdated(state.webinarData));
            this.dispatch(meUpdated(state.me));

            this.dispatch(chaptersUpdated(state.chapters));
            this.dispatch(activeChapterUpdated(state.activeChapterId));

            this.dispatch(activePollUpdated({ activePollId: state.activePollId, activePollStatus: state.activePollStatus }));

            this.dispatch(activeSlideUpdated(state.activeSlideNumber));
            this.dispatch(slideDeckUpdated(state.slideDeck));

            this.dispatch(liveStatusUpdated(state.liveStatus));
            this.dispatch(streamValidationUpdated(state.streamValidation));
            this.dispatch(ingestTypeHydrated(state.ingestType));
            this.dispatch(videoLayoutUpdated(state.videoLayout));

            this.dispatch(surveyUpdated(state.survey));
            this.dispatch(surveyAnswersUpdated(state.surveyAnswers));
            this.dispatch(examUpdated(state.exam));
            this.dispatch(examAnswersUpdated(state.examAnswers));

            this.dispatch(myPollVotes(state.pollVotes));

            this.dispatch(updateSubtitlesTracks(state.subtitlesTracks));

            this.dispatch(vindralChannelUpdated(state.vindralChannelInformation));
            this.dispatch(initializedWebinar());
        });

        const connectionCheckInterval = setInterval(() => {
            if (this.connection !== connection || !this.isConnected) {
                clearInterval(connectionCheckInterval);
                return;
            }

            if (
                this.connection.state === HubConnectionState.Connected ||
                this.connection.state === HubConnectionState.Connecting ||
                this.connection.state === HubConnectionState.Reconnecting
            ) {
                return;
            }

            this.connection.start().then(
                () => {
                    this.dispatch(dataConnectionStatusUpdated({ status: LiveStatus.Live }));
                    this.notifyConnected();
                },
                (reason) => {
                    this.dispatch(
                        dataConnectionStatusUpdated({
                            status: this.disconnectReason === DisconnectReason.Error ? LiveStatus.Error : LiveStatus.Offline,
                            error: this.error || {
                                statusCode: 500,
                                success: false,
                                message: reason?.message || 'Connection failed'
                            },
                            disconnectReason: this.disconnectReason
                        })
                    );
                }
            );
        }, 1000);

        this.disconnectReason = undefined;
        this.error = undefined;
        this.isConnected = true;
        return new Promise<boolean>((resolve, reject) => {
            connection
                .start()
                .then(() => {
                    if (this.connection !== connection) {
                        resolve(false);
                        return;
                    }
                    this.dispatch(dataConnectionStatusUpdated({ status: LiveStatus.Live }));
                    resolve(true);
                })
                .catch((reason) => {
                    this.dispatch(
                        dataConnectionStatusUpdated({
                            status: LiveStatus.Offline,
                            error: {
                                statusCode: 500,
                                success: false,
                                message: reason?.message || 'Connection failed'
                            },
                            disconnectReason: this.disconnectReason
                        })
                    );
                    resolve(false);
                });
        });
    }

    startWebinar(): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('StartWebinar');
    }

    stopWebinar(): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('StopWebinar');
    }

    claimActivePresenter(): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('ClaimActivePresenter');
    }

    setStatus(status: SpeakerStatus): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SetStatus', status);
    }

    kickSpeaker(participantId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('KickSpeaker', participantId);
    }

    notifySpeaker(participantId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('NotifySpeaker', participantId);
    }

    joinChatChannel(channelId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('JoinChatChannel', channelId);
    }

    leaveChatChannel(channelId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('LeaveChatChannel', channelId);
    }

    deleteChatMessage(channelId: string, messageId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('DeleteChatMessage', channelId, messageId);
    }

    sendChatMessage(channelId: string, message: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SendChatMessage', channelId, message);
    }

    replyChatMessage(channelId: string, message: string, linkedMessageId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('ReplyChatMessage', channelId, message, linkedMessageId);
    }

    forwardChatMessage(channelId: string, message: string, linkedMessageId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('ForwardChatMessage', channelId, message, linkedMessageId);
    }

    markChatChannelAsRead(channelId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('MarkChatChannelAsRead', channelId);
    }

    markChatChannelAsUnread(channelId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('MarkChatChannelAsUnread', channelId);
    }

    /**
     * Tell the server which connection is ours now, and what happened to our video while we were away.
     *
     * Called after every (re)connect. The server keeps the broadcast on air for a few seconds after
     * losing us, so this is what claims that grace -- and reporting an interruption here is what stops
     * a presenter whose camera dropped from silently coming back as if nothing happened.
     */
    registerBroadcastConnection(mediaInterrupted: boolean, mediaFlowing: boolean): Promise<ApiResponse<LiveStatus>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('RegisterBroadcastConnection', mediaInterrupted, mediaFlowing);
    }

    reportMediaLost(): Promise<ApiResponse<LiveStatus>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('ReportMediaLost');
    }

    reportMediaRestored(): Promise<ApiResponse<LiveStatus>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('ReportMediaRestored');
    }

    setLiveStatus(status: LiveStatus): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SetLiveStatus', status);
    }

    setVideoLayout(layout: VideoLayout): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SetVideoLayout', layout);
    }

    setActiveSlide(slideNumber: number): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SetActiveSlide', slideNumber);
    }

    setActiveChapter(chapterId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SetActiveChapter', chapterId);
    }

    refreshVindralChannel(): Promise<ApiResponse<VindralChannelInformation | null>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('RefreshVindralChannel');
    }

    getRtmpIngestInformation(): Promise<ApiResponse<IngestInformation>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('GetRtmpIngestInformation');
    }

    getSrtIngestInformation(): Promise<ApiResponse<IngestInformation>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('GetSrtIngestInformation');
    }

    startPoll(pollId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('StartPoll', pollId);
    }

    closePoll(): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('ClosePoll');
    }

    hidePoll(): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('HidePoll');
    }

    voteOnPoll(pollId: string, optionId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('VoteOnPoll', pollId, optionId);
    }

    submitSurveyAnswer(questionId: string, openAnswer: string | null, selectedAnswers: string[] | null): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SubmitSurveyAnswer', questionId, openAnswer, selectedAnswers);
    }

    retryExam(): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('RetryExam');
    }

    submitExamAnswer(questionId: string, openAnswer: string | null, selectedAnswers: string[] | null): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SubmitExamAnswer', questionId, openAnswer, selectedAnswers);
    }

    getExamResult(): Promise<ApiResponse<ExamResult>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('GetExamResult');
    }

    updateNickname(nickname: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('UpdateNickname', nickname);
    }

    sendWebrtcIngestStats(stats: IWebrtcIngestStats): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('WebrtcIngestStats', stats);
    }

    sendMoqPublisherStats(sample: MoqPublisherStatsSample): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('MoqPublisherStats', sample);
    }

    /** The hub is connected right now; `isConnected` only says that a connection is wanted. */
    get hubConnected(): boolean {
        return this.connection?.state === HubConnectionState.Connected;
    }

    lineProbeSession(): Promise<ApiResponse<SessionGrant>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('LineProbeSession');
    }

    lineProbeStats(batch: LineProbeStatsBatch): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('LineProbeStats', batch);
    }

    /**
     * Callbacks run after the hub (re)connected, next to the player stats flush. Returns the
     * unsubscribe function. The broadcaster's line probe registers its queue flush here.
     */
    onConnected(callback: () => void): () => void {
        this.connectedCallbacks.add(callback);
        return () => this.connectedCallbacks.delete(callback);
    }

    private notifyConnected(): void {
        void this.playerStats.flush();
        for (const callback of this.connectedCallbacks) {
            try {
                callback();
            } catch {
                // A failing subscriber must not block the others
            }
        }
    }

    queuePlayerStats(sample: PlayerStatsSample): void {
        this.playerStats.push(sample);
    }

    setIngestType(ingestType: IngestType): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SetIngestType', ingestType);
    }

    extendWebinarDuration(duration: Duration): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('ExtendWebinarDuration', duration.toFormat('hh:mm:ss'));
    }

    disconnect(): void {
        if (!this.connection) {
            return;
        }

        // Make sure a connect (while disconnecting) causes a new instance to be created
        const connection = this.connection;
        this.connection = undefined;

        connection
            .stop()
            .then(() => {
                // TODO: Should we dispatch an event?  Pretty sure "onclose" will do that
            })
            .catch(() => {
                // TODO: Should we do something here or just ignore it?
            });
    }
}
